/**
 * Trình đơn Xuất trên trang phiếu — port `exportMenu` của bản mẫu (~dòng 1373-1383,
 * `exportAnswersJson`/`exportAnswersCsv`/`exportStructureJson`/`exportXlsForm`).
 *
 * Khác bản mẫu ở một điểm quan trọng: XLSForm được **máy chủ** sinh từ phiên bản
 * đang active (`GET /export/xlsform/:formKey`, chỉ admin), không dựng lại ở trình
 * duyệt — tránh việc mỗi máy xuất ra một file khác nhau. Ba mục còn lại là dữ liệu
 * của chính phiếu đang mở nên sinh tại chỗ, dùng được cả khi mất mạng.
 */
import * as api from '../api-client.js';
import { el, esc, toast, downloadBlob as download } from './util.js';

/** Ô bắt đầu bằng = + - @ bị Excel coi là công thức — vô hiệu hoá bằng dấu nháy đơn. */
function csvCell(v) {
  let s = String(v ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

/**
 * @param {{ button: HTMLElement, submission: any, getAnswers: () => Record<string,string>,
 *           masterData: Record<string,string>, canExportXlsForm: boolean }} opts
 */
export function createExportMenu({ button, submission, getAnswers, masterData, canExportXlsForm }) {
  const baseName = `${submission.formKey}-${submission.id.slice(0, 8)}`;

  const items = [
    {
      key: 'answers-json',
      icon: 'data_object',
      title: 'Xuất câu trả lời (JSON)',
      sub: 'Toàn bộ dữ liệu đã nhập, kèm khoá theo bản ghi lặp',
    },
    {
      key: 'answers-csv',
      icon: 'table_view',
      title: 'Xuất câu trả lời (CSV)',
      sub: 'Mở trực tiếp bằng Excel để đối chiếu',
    },
    {
      key: 'structure-json',
      icon: 'account_tree',
      title: 'Xuất cấu trúc phiếu (JSON)',
      sub: 'Đúng bộ câu hỏi phiếu này đang dùng (form_schema_snapshot)',
    },
    ...(canExportXlsForm
      ? [
          {
            key: 'xlsform',
            icon: 'description',
            title: 'Xuất XLSForm (.xlsx)',
            sub: '3 sheet survey / choices / settings — nạp thẳng lên ODK Central',
          },
        ]
      : []),
  ];

  const menu = el('div', 'export-menu');
  menu.hidden = true;
  menu.setAttribute('role', 'menu');
  menu.innerHTML = items
    .map(
      (i) => `<button type="button" role="menuitem" data-export="${i.key}">
        <span class="ms">${i.icon}</span>
        <span>${esc(i.title)}<span class="em-sub">${esc(i.sub)}</span></span>
      </button>`,
    )
    .join('');
  document.body.appendChild(menu);

  function close() {
    menu.hidden = true;
    button.setAttribute('aria-expanded', 'false');
  }
  function open() {
    const r = button.getBoundingClientRect();
    menu.hidden = false;
    // Neo mép phải của trình đơn vào mép phải của nút, kẹp trong khung nhìn.
    menu.style.top = `${r.bottom + 6}px`;
    menu.style.left = `${Math.max(8, Math.min(r.right - menu.offsetWidth, window.innerWidth - menu.offsetWidth - 8))}px`;
    button.setAttribute('aria-expanded', 'true');
  }

  button.addEventListener('click', (e) => {
    e.stopPropagation();
    if (menu.hidden) open();
    else close();
  });
  const onDocClick = () => close();
  const onKey = (e) => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);

  menu.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-export]');
    if (!btn) return;
    close();
    try {
      await run(btn.dataset.export);
    } catch (err) {
      toast('Không xuất được: ' + (err?.message || err));
    }
  });

  async function run(kind) {
    const answers = getAnswers();

    if (kind === 'answers-json') {
      const payload = {
        exportedAt: new Date().toISOString(),
        submissionId: submission.id,
        formKey: submission.formKey,
        status: submission.status,
        // Tách riêng để người đọc file biết đâu là câu trả lời, đâu là hồ sơ
        // Hộ/Vườn được chiếu vào (R1) — hai thứ có vòng đời khác nhau.
        masterData,
        answers,
      };
      download(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }), `${baseName}-answers.json`);
      toast(`Đã xuất ${Object.keys(answers).length} trường dữ liệu`);
      return;
    }

    if (kind === 'answers-csv') {
      const rows = [['bản ghi', 'biến', 'giá trị']];
      Object.keys(answers)
        .sort()
        .forEach((k) => {
          const v = answers[k];
          if (v === undefined || String(v).trim() === '') return;
          const parts = k.split('.');
          const name = parts.pop();
          rows.push([parts.join('.') || '-', name, String(v)]);
        });
      const csv = '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
      download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${baseName}-answers.csv`);
      toast(`Đã xuất ${rows.length - 1} dòng dữ liệu`);
      return;
    }

    if (kind === 'structure-json') {
      download(
        new Blob([JSON.stringify(submission.formSchemaSnapshot, null, 2)], { type: 'application/json' }),
        `${baseName}-structure.json`,
      );
      toast('Đã xuất cấu trúc bộ câu hỏi của phiếu');
      return;
    }

    if (kind === 'xlsform') {
      const blob = await api.exportApi.xlsform(submission.formKey);
      download(blob, `${submission.formKey}-xlsform.xlsx`);
      toast('Đã xuất XLSForm của phiên bản đang áp dụng');
    }
  }

  return {
    close,
    destroy() {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
      menu.remove();
    },
  };
}
